import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { AlertCircle, TrendingDown, CreditCard, Calendar, PieChart, Frown } from 'lucide-react';

const problems = [
  {
    icon: AlertCircle,
    text: 'Falta de controle financeiro'
  },
  {
    icon: TrendingDown,
    text: 'Dívidas acumulando'
  },
  {
    icon: CreditCard,
    text: 'Ansiedade com boletos'
  },
  {
    icon: Calendar,
    text: 'Saldo negativo constante'
  },
  {
    icon: PieChart,
    text: 'Falta de planejamento'
  },
  {
    icon: Frown,
    text: 'Dinheiro nunca sobra no fim do mês'
  }
];

export const ProblemSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <section ref={ref} className="py-20 sm:py-32 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-card/30 to-background" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Section Title */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Cansado de{' '}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-destructive to-warning">
                nunca ter dinheiro
              </span>
              {' '}sobrando?
            </h2>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              Você não está sozinho. Milhares de pessoas enfrentam os mesmos problemas financeiros todos os dias.
            </p>
          </motion.div>

          {/* Problems Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {problems.map((problem, index) => {
              const Icon = problem.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group"
                >
                  <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-6 hover:border-destructive/30 transition-all duration-300 hover:shadow-lg hover:shadow-destructive/10">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-lg bg-destructive/10 flex items-center justify-center group-hover:bg-destructive/20 transition-colors">
                          <Icon className="w-6 h-6 text-destructive" />
                        </div>
                      </div>
                      <p className="text-foreground leading-relaxed pt-2">
                        {problem.text}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Emotional closing */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-16 text-center"
          >
            <p className="text-xl sm:text-2xl text-muted-foreground font-medium">
              É hora de{' '}
              <span className="text-primary font-semibold">mudar essa história</span>
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
